import { z } from 'zod';
import { resumeTemplateIds, visitorAnalyticsEventNames } from './types';

export const languageSchema = z.enum(['en', 'ar']);
export const careerStageSchema = z.enum([
  'student',
  'fresh_graduate',
  'professional',
  'career_switcher',
]);
export const primaryGoalSchema = z.enum([
  'first_cv',
  'improve_cv',
  'improve_linkedin',
  'prepare_interview',
  'specific_job',
]);

const optionalUrl = z.union([z.string().url(), z.literal('')]);
const optionalShortText = z.string().trim().max(300).default('');

export const personalDetailsSchema = z.object({
  fullName: z.string().trim().max(120).default(''),
  email: z.union([z.string().email(), z.literal('')]).default(''),
  phone: z.string().trim().max(40).default(''),
  city: optionalShortText,
  country: optionalShortText,
  linkedinUrl: optionalUrl.default(''),
  portfolioUrl: optionalUrl.default(''),
});

export const targetDetailsSchema = z.object({
  role: z.string().trim().max(160).default(''),
  industry: optionalShortText,
  country: optionalShortText,
  seniority: z.union([
    z.enum(['intern', 'entry', 'mid', 'senior', 'lead', 'manager']),
    z.literal(''),
  ]),
});

export const educationSchema = z.object({
  id: z.string().uuid().optional(),
  institution: z.string().trim().max(200),
  degree: z.string().trim().max(160),
  field: z.string().trim().max(160),
  startDate: z.string().max(20),
  endDate: z.string().max(20),
  grade: z.string().trim().max(80),
  description: z.string().trim().max(2_000),
});

export const experienceSchema = z.object({
  id: z.string().uuid().optional(),
  company: z.string().trim().max(200),
  jobTitle: z.string().trim().max(160),
  location: z.string().trim().max(160),
  startDate: z.string().max(20),
  endDate: z.string().max(20),
  current: z.boolean(),
  bullets: z.array(z.string().trim().min(1).max(600)).max(12),
});

export const projectSchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string().trim().max(200),
  role: z.string().trim().max(160),
  description: z.string().trim().max(2_000),
  technologies: z.array(z.string().trim().min(1).max(80)).max(30),
  result: z.string().trim().max(600),
});

export const skillSchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string().trim().min(1).max(100),
  category: z.enum(['technical', 'software', 'soft', 'tools']),
  level: z.enum(['beginner', 'intermediate', 'advanced']).optional(),
});

export const certificationSchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string().trim().max(200),
  issuer: z.string().trim().max(200),
  issueDate: z.string().max(20),
  credentialUrl: optionalUrl,
});

export const spokenLanguageSchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string().trim().max(80),
  proficiency: z.enum(['basic', 'conversational', 'professional', 'native']),
});

export const achievementSchema = z.object({
  id: z.string().uuid().optional(),
  title: z.string().trim().max(200),
  description: z.string().trim().max(1_000),
  date: z.string().max(20),
});

export const careerProfileSchema = z.object({
  careerStage: careerStageSchema,
  preferredLanguage: languageSchema,
  primaryGoal: primaryGoalSchema,
  personal: personalDetailsSchema,
  target: targetDetailsSchema,
  summary: z.string().trim().max(2_000),
  education: z.array(educationSchema).max(20),
  experiences: z.array(experienceSchema).max(30),
  projects: z.array(projectSchema).max(30),
  skills: z.array(skillSchema).max(100),
  certifications: z.array(certificationSchema).max(40),
  languages: z.array(spokenLanguageSchema).max(20),
  achievements: z.array(achievementSchema).max(30),
});

export const resumeSchema = z.object({
  id: z.string().uuid().optional(),
  title: z.string().trim().min(1).max(160),
  template: z.enum(resumeTemplateIds),
  language: languageSchema,
  sectionOrder: z.array(
    z.enum([
      'summary',
      'experience',
      'education',
      'projects',
      'skills',
      'certifications',
      'languages',
      'achievements',
    ]),
  ),
  hiddenSections: z.array(
    z.enum([
      'summary',
      'experience',
      'education',
      'projects',
      'skills',
      'certifications',
      'languages',
      'achievements',
    ]),
  ),
  profile: careerProfileSchema,
});

export const linkedinProfileSchema = z.object({
  headline: z.string().trim().max(220),
  about: z.string().trim().max(4_000),
  experienceText: z.string().trim().max(12_000),
  educationText: z.string().trim().max(6_000),
  skills: z.array(z.string().trim().min(1).max(100)).max(100),
  projectsText: z.string().trim().max(6_000),
  certificatesText: z.string().trim().max(6_000),
  pastedProfileText: z.string().trim().max(24_000),
});

export const jobSchema = z.object({
  id: z.string().uuid().optional(),
  title: z.string().trim().min(1).max(200),
  company: z.string().trim().max(200),
  description: z.string().trim().min(80).max(30_000),
  language: languageSchema.default('en'),
});

export const registerSchema = z.object({
  fullName: z.string().trim().min(2).max(120),
  email: z
    .string()
    .trim()
    .email()
    .max(254)
    .transform((value) => value.toLowerCase()),
  password: z
    .string()
    .min(10)
    .max(128)
    .regex(/[a-z]/, 'Password must include a lowercase letter')
    .regex(/[A-Z]/, 'Password must include an uppercase letter')
    .regex(/[0-9]/, 'Password must include a number'),
  preferredLanguage: languageSchema.default('en'),
  turnstileToken: z.string().max(2_048).optional(),
});

export const strongPasswordSchema = z
  .string()
  .min(10)
  .max(128)
  .regex(/[a-z]/, 'Password must include a lowercase letter')
  .regex(/[A-Z]/, 'Password must include an uppercase letter')
  .regex(/[0-9]/, 'Password must include a number');

export const completePasswordSetupSchema = z.object({
  password: strongPasswordSchema,
});

const adminAuthenticatorCodeSchema = z
  .string()
  .trim()
  .regex(/^\d{6}$/u);

export const adminTotpEnrollmentBeginSchema = z.object({
  email: z
    .string()
    .trim()
    .email()
    .max(254)
    .transform((value) => value.toLowerCase()),
  fullName: z.string().trim().min(2).max(120),
  bootstrapCode: z.string().trim().min(16).max(160),
});

export const adminTotpEnrollmentCompleteSchema = z.object({
  enrollmentToken: z.string().min(32).max(160),
  code: adminAuthenticatorCodeSchema,
});

export const adminTotpLoginSchema = z.object({
  email: z
    .string()
    .trim()
    .email()
    .max(254)
    .transform((value) => value.toLowerCase()),
  code: adminAuthenticatorCodeSchema,
});

export const adminIntakeFormSchema = z.object({
  name: z.string().trim().min(2).max(120),
  language: languageSchema,
});

export const whatsappSettingsSchema = z.object({
  enabled: z.boolean(),
  autoReplyEnabled: z.boolean(),
  sendFormOnApproval: z.boolean(),
  assistantName: z.string().trim().min(2).max(80),
  businessDescription: z.string().trim().min(10).max(2_000),
  serviceName: z.string().trim().min(2).max(120),
  servicePriceMinor: z.number().int().min(0).max(10_000_000),
  currency: z
    .string()
    .trim()
    .regex(/^[A-Z]{3}$/u),
  defaultLanguage: z.enum(['auto', 'en', 'ar']),
  textModel: z.enum(['@cf/google/gemma-4-26b-a4b-it', '@cf/zai-org/glm-4.7-flash']),
  visionModel: z.literal('@cf/google/gemma-4-26b-a4b-it'),
  transcriptionModel: z.literal('@cf/openai/whisper-large-v3-turbo'),
  graphApiVersion: z
    .string()
    .trim()
    .regex(/^v[0-9]{1,2}\.[0-9]$/u),
  phoneNumberId: z
    .string()
    .trim()
    .max(80)
    .regex(/^$|^[0-9]+$/u),
  wabaId: z
    .string()
    .trim()
    .max(80)
    .regex(/^$|^[0-9]+$/u),
  systemPrompt: z.string().trim().min(40).max(8_000),
  knowledgeBase: z.string().trim().min(10).max(20_000),
  paymentInstructionsEn: z.string().trim().min(10).max(4_000),
  paymentInstructionsAr: z.string().trim().min(10).max(4_000),
  formMessageEn: z
    .string()
    .trim()
    .min(10)
    .max(4_000)
    .refine((value) => value.includes('{url}'), {
      message: 'The English form message must include {url}',
    }),
  formMessageAr: z
    .string()
    .trim()
    .min(10)
    .max(4_000)
    .refine((value) => value.includes('{url}'), {
      message: 'The Arabic form message must include {url}',
    }),
  completionMessageEn: z.string().trim().min(10).max(4_000),
  completionMessageAr: z.string().trim().min(10).max(4_000),
  handoffMessageEn: z.string().trim().min(10).max(2_000),
  handoffMessageAr: z.string().trim().min(10).max(2_000),
  maxContextMessages: z.number().int().min(2).max(30),
  mediaRetentionDays: z.number().int().min(7).max(365),
});

export const whatsappManualMessageSchema = z.object({
  text: z.string().trim().min(1).max(4_096),
});

export const whatsappConversationControlSchema = z.object({
  paused: z.boolean(),
});

export const whatsappPaymentDecisionSchema = z.object({
  note: z.string().trim().max(1_000).default(''),
  customerName: z.string().trim().min(2).max(120).optional(),
  language: languageSchema.optional(),
});

const publicPhoneSchema = z
  .string()
  .trim()
  .min(8)
  .max(24)
  .regex(/^\+?[0-9][0-9\s()-]{7,23}$/u, 'Enter a valid phone number');

export const publicIntakeCompletionSchema = z.object({
  profile: careerProfileSchema.superRefine((profile, context) => {
    if (!profile.personal.fullName.trim()) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['personal', 'fullName'],
        message: 'Name is required',
      });
    }
    if (!profile.personal.email.trim()) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['personal', 'email'],
        message: 'Email is required',
      });
    }
    const phone = publicPhoneSchema.safeParse(profile.personal.phone);
    if (!phone.success) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['personal', 'phone'],
        message: 'A valid phone number is required',
      });
    }
    if (!profile.target.role.trim()) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['target', 'role'],
        message: 'Target role is required',
      });
    }
    if (profile.skills.length === 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['skills'],
        message: 'Add at least one skill',
      });
    }
  }),
  submissionToken: z.string().regex(/^[A-Za-z0-9_-]{43,160}$/u),
  turnstileToken: z.string().max(2_048).optional(),
  consented: z.literal(true),
});

export const loginSchema = z.object({
  email: z
    .string()
    .trim()
    .email()
    .max(254)
    .transform((value) => value.toLowerCase()),
  password: z.string().min(1).max(128),
});

export const onboardingSchema = z.object({
  careerStage: careerStageSchema,
  targetRole: z.string().trim().max(160),
  preferredLanguage: languageSchema,
  primaryGoal: primaryGoalSchema,
});

export const aiActionSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('cv_rewrite'),
    input: z.object({ text: z.string().trim().min(4).max(1_500), context: z.string().max(4_000) }),
    outputLanguage: languageSchema,
    regenerate: z.boolean().default(false),
  }),
  z.object({
    action: z.literal('linkedin_headline'),
    input: z.object({ headline: z.string().max(500), profileContext: z.string().max(8_000) }),
    outputLanguage: languageSchema,
    regenerate: z.boolean().default(false),
  }),
  z.object({
    action: z.literal('linkedin_about'),
    input: z.object({ about: z.string().max(5_000), profileContext: z.string().max(8_000) }),
    outputLanguage: languageSchema,
    regenerate: z.boolean().default(false),
  }),
  z.object({
    action: z.literal('interview_pack'),
    input: z.object({ profile: z.string().max(12_000), job: z.string().max(20_000) }),
    outputLanguage: languageSchema,
    regenerate: z.boolean().default(false),
  }),
]);

const analyticsHostnameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(253)
  .regex(
    /^(?=.{1,253}$)(?:(?!-)[a-z0-9-]{1,63}(?<!-)\.)+(?:[a-z]{2,63})$/u,
    'Enter a hostname only.',
  );

const analyticsAttributionValueSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(80)
  .regex(/^[a-z0-9][a-z0-9_-]{0,79}$/u, 'Use a short normalized value.');

function containsControlCharacter(value: string): boolean {
  return Array.from(value).some((character) => character.charCodeAt(0) < 32);
}

const analyticsPathSchema = z
  .string()
  .trim()
  .min(1)
  .max(512)
  .refine(
    (value) =>
      value.startsWith('/') &&
      !value.startsWith('//') &&
      !value.includes('?') &&
      !value.includes('#') &&
      !containsControlCharacter(value),
    'Use a pathname without a query string or fragment.',
  );

/**
 * First-party visitor analytics intentionally accepts only anonymous, aggregate-safe fields.
 * Do not add user IDs, IP addresses, raw URLs, or arbitrary metadata here.
 */
export const visitorAnalyticsEventSchema = z
  .object({
    sessionId: z.string().uuid(),
    event: z.enum(visitorAnalyticsEventNames),
    path: analyticsPathSchema,
    durationMs: z
      .number()
      .int()
      .min(0)
      .max(30 * 60 * 1_000)
      .optional(),
    referrerHost: analyticsHostnameSchema.optional(),
    utmSource: analyticsAttributionValueSchema.optional(),
    utmMedium: analyticsAttributionValueSchema.optional(),
    utmCampaign: analyticsAttributionValueSchema.optional(),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.event === 'page_leave' && value.durationMs === undefined) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['durationMs'],
        message: 'Duration is required for page-leave events.',
      });
    }
    if (value.event !== 'page_leave' && value.durationMs !== undefined) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['durationMs'],
        message: 'Duration is only accepted for page-leave events.',
      });
    }
  });

export type CareerProfilePayload = z.infer<typeof careerProfileSchema>;
export type ResumePayload = z.infer<typeof resumeSchema>;
export type VisitorAnalyticsEventPayload = z.infer<typeof visitorAnalyticsEventSchema>;
